import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CalendarDays, Plus, Check, Pencil, Trash2, Clock, User,
} from "lucide-react";
import { toast } from "sonner";

/* Convert UTC ISO -> local YYYY-MM-DDTHH:MM for datetime-local input */
function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

function fmtDT(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });
}

function groupByDay(items) {
  const map = new Map();
  for (const it of items) {
    const d = new Date(it.when_at);
    const key = new Intl.DateTimeFormat("pt-BR", {
      weekday: "long", day: "2-digit", month: "long",
      timeZone: "America/Sao_Paulo",
    }).format(d);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(it);
  }
  return Array.from(map.entries()).map(([label, list]) => ({ label, items: list }));
}

const EMPTY = { title: "", when_at: "", notes: "", client_id: "" };

function AgendaFormDialog({ open, onOpenChange, editing, clients, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        title: editing.title || "",
        when_at: toLocalInput(editing.when_at),
        notes: editing.notes || "",
        client_id: editing.client_id || "",
      });
    } else {
      setForm(EMPTY);
    }
  }, [editing, open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.when_at) {
      toast.error("Preencha título e data/horário");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        when_at: new Date(form.when_at).toISOString(),
        notes: form.notes?.trim() || "",
        client_id: form.client_id || null,
        completed: editing?.completed || false,
      };
      if (editing?.agenda_id) {
        await api.put(`/agenda/${editing.agenda_id}`, payload);
        toast.success("Compromisso atualizado");
      } else {
        await api.post("/agenda", payload);
        toast.success("Compromisso criado");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="agenda-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar compromisso" : "Novo compromisso"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Título</Label>
            <Input value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Ex.: Emitir passagem João Silva"
              data-testid="agenda-title" />
          </div>
          <div>
            <Label>Data e horário</Label>
            <Input type="datetime-local" value={form.when_at}
              onChange={(e) => setForm((f) => ({ ...f, when_at: e.target.value }))}
              data-testid="agenda-when" />
          </div>
          <div>
            <Label>Cliente (opcional)</Label>
            <Select value={form.client_id || "none"}
              onValueChange={(v) => setForm((f) => ({ ...f, client_id: v === "none" ? "" : v }))}>
              <SelectTrigger data-testid="agenda-client">
                <SelectValue placeholder="Vincular a um cliente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhum</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.client_id} value={c.client_id}>{c.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Notas (opcional)</Label>
            <Textarea rows={3} value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              data-testid="agenda-notes" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline"
              onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving}
              className="brand-grad text-white" data-testid="agenda-save">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AgendaTab() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [filter, setFilter] = useState("upcoming");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: ag }, { data: cs }] = await Promise.all([
        api.get("/agenda", { params: { scope: filter } }),
        api.get("/clients"),
      ]);
      setItems(ag);
      setClients(cs);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const handleToggle = async (it) => {
    try {
      await api.patch(`/agenda/${it.agenda_id}/toggle`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/agenda/${deleting.agenda_id}`);
      toast.success("Compromisso excluído");
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const groups = groupByDay(items);

  return (
    <div data-testid="agenda-tab">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-[#0b3a82]" /> Agenda
          </h2>
          <p className="text-slate-500 text-sm">Organize compromissos, emissões e follow-ups.</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[160px] bg-white" data-testid="agenda-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Hoje</SelectItem>
              <SelectItem value="upcoming">Próximos</SelectItem>
              <SelectItem value="past">Passados</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => { setEditing(null); setOpenForm(true); }}
            className="brand-grad text-white"
            data-testid="add-agenda-button">
            <Plus className="w-4 h-4 mr-1" /> Novo compromisso
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500 text-center py-12" data-testid="agenda-loading">Carregando...</div>
      ) : items.length === 0 ? (
        <div className="soft-card p-10 text-center" data-testid="agenda-empty">
          <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-3" strokeWidth={1.4} />
          <div className="text-lg font-semibold text-slate-900">Sem compromissos</div>
          <p className="text-slate-500 mt-1 text-sm">Comece adicionando um compromisso.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.label} className="soft-card p-5">
              <div className="text-xs uppercase tracking-wide font-semibold text-slate-400 mb-3">
                {g.label}
              </div>
              <ul className="space-y-2">
                {g.items.map((it) => (
                  <li key={it.agenda_id}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${
                      it.completed ? "bg-slate-50 border-slate-200" : "bg-white border-slate-200 hover:border-blue-200"
                    }`}
                    data-testid={`agenda-item-${it.agenda_id}`}>
                    <button
                      type="button"
                      onClick={() => handleToggle(it)}
                      className={`mt-0.5 w-5 h-5 rounded-md border-2 grid place-items-center shrink-0 ${
                        it.completed
                          ? "bg-emerald-500 border-emerald-500 text-white"
                          : "border-slate-300 hover:border-[#0b3a82]"
                      }`}
                      data-testid={`agenda-toggle-${it.agenda_id}`}
                      title={it.completed ? "Desmarcar" : "Marcar como concluído"}>
                      {it.completed && <Check className="w-3 h-3" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium ${it.completed ? "text-slate-400 line-through" : "text-slate-900"}`}>
                        {it.title}
                      </div>
                      <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDT(it.when_at)}</span>
                        {it.client_name && (
                          <span className="flex items-center gap-1"><User className="w-3 h-3" /> {it.client_name}</span>
                        )}
                      </div>
                      {it.notes && (
                        <div className="text-xs text-slate-600 mt-2 whitespace-pre-line">{it.notes}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="ghost"
                        onClick={() => { setEditing(it); setOpenForm(true); }}
                        data-testid={`agenda-edit-${it.agenda_id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleting(it)}
                        data-testid={`agenda-delete-${it.agenda_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <AgendaFormDialog open={openForm} onOpenChange={setOpenForm}
        editing={editing} clients={clients} onSaved={load} />

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir compromisso?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{deleting?.title}&quot; será removido permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
